import { impactArrow, type FramingImpact } from "../lib/caption-impacts.js";

/**
 * Compact "this does that" impact-chip row, shared by EVERY emotion-affecting
 * control (captions, styles, looks, effects, sound, stickers, focus, cut). Each
 * chip shows an emotion axis it moves (with ↑/↑↑ magnitude) or the cred tradeoff
 * (`cred ↓` receipts / `cred ↑`), in the SAME visual language across tools. The
 * chips are DERIVED from the scenario's landing levers (see caption-impacts.ts),
 * so what a chip promises equals what publishing does. Presentation only.
 */
export function ImpactChips({
  impacts,
  testId,
  minimalLabel = "minimal",
  className,
}: {
  impacts: FramingImpact[];
  testId?: string;
  /** Fallback pill when a control has no landing impact (null = render nothing). */
  minimalLabel?: string | null;
  className?: string;
}) {
  return (
    <span
      className={`impact-chips${className ? ` ${className}` : ""}`}
      data-testid={testId}
    >
      {impacts.length === 0 ? (
        minimalLabel ? (
          <span className="impact-chip impact-tone-reach">{minimalLabel}</span>
        ) : null
      ) : (
        impacts.map((im) => (
          <span
            key={im.key}
            className={`impact-chip impact-tone-${im.tone}`}
            data-dir={im.dir}
          >
            {im.label}{" "}
            <span className="impact-arrow" aria-hidden>
              {impactArrow(im)}
            </span>
          </span>
        ))
      )}
    </span>
  );
}

/** Accessible sentence describing a chip row (for aria-labels). */
export function impactAriaLabel(impacts: FramingImpact[]): string {
  return impacts.map((im) => `${im.label} ${im.dir === "up" ? "up" : "down"}`).join(", ");
}
